import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LongSteelOrdersService } from '../../../../../../../services/LongSteelOrders';
import {
  EModalMessageErrorType,
  IOpenOrderData,
  IUpdateOrderRequest,
  IUpdateOrderResponse,
  IUpdateOrderValidation
} from '../../../../../../../store/ducks/long-steel-orders/types';
import { addToast } from '../../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../../store/ducks/toast/types';
import { ECustomerTypeView } from '../../../../../../../store/ducks/user/types';
import CancelOrderModal from './CancelOderModal';
import CancelOrderModalReason from './CancelOrderModalReason';
import { AxiosError } from 'axios';
import WarningCancelOrderModal from './WarningCancelOrderModal';

interface ICancelOrderProps {
  order: IOpenOrderData;
  onClose: () => void;
  customerViewType: ECustomerTypeView;
}

const CancelOrder: React.FC<ICancelOrderProps> = ({
  order,
  onClose,
  customerViewType
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [cancelReasonMessage, setCancelReasonMessage] = useState<string>('');
  const [showErrorReason, setShowErrorReason] = useState<boolean>(false);
  const [openCancelReasonModal, setOpenCancelReasonModal] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const handleValidation = async (): Promise<void | {
    changeAllowed: boolean;
    reasonNotAllowed?: string;
  }> => {
    setLoading(true);
    const customerId =
      customerViewType === ECustomerTypeView.EMITTER
        ? order.soldTo.code
        : order.shipTo.code;

    const bodyRequest: IUpdateOrderValidation[] = [
      {
        customer: customerId,
        orderId: order.id
      }
    ];

    const getChangeTypeAllowed =
      await LongSteelOrdersService.validationToUpdate(bodyRequest)
        .then((response) => {
          const { changeAllowed, reasonNotAllowed } = response[0];
          return { changeAllowed, reasonNotAllowed };
        })
        .catch(() => {
          dispatch(
            addToast({
              message: 'Ocorreu um erro com a validacao do seu pedido.',
              type: EToastTypes.DANGER
            })
          );
        })
        .finally(() => {
          setLoading(false);
        });

    return getChangeTypeAllowed;
  };

  const handleCancelOrder = async () => {
    if (cancelReasonMessage.length < 1 || cancelReasonMessage.length > 50) {
      setShowErrorReason(true);
      return;
    }
    setLoading(true);

    const cancelMessage = cancelReasonMessage.trim();

    const requestBody: IUpdateOrderRequest[] = [
      {
        customer: order.soldTo.code,
        customerType: ECustomerTypeView.EMITTER,
        salesOrder: order.salesOrder.toString(),
        salesOrderItem: order.salesOrderItem.toString(),
        status: 'cancelado',
        reasonMessage: cancelMessage.trim()
      }
    ];

    LongSteelOrdersService.sendOrderUpdateData(requestBody)
      .then((response) => {
        setLoading(false);
        if (
          response?.every((item) => item.requestEditStatus === 'processando')
        ) {
          dispatch(
            addToast({
              message:
                'Cancelamento solicitado com sucesso. Fique atento ao seu email de acesso.',
              type: EToastTypes.SUCCESS
            })
          );
          onCloseReasonModal();
        }
      })
      .catch((error: AxiosError<{ items: IUpdateOrderResponse[] }>) => {
        setLoading(false);
        if (error.response?.status === 422) {
          setErrorMessage(error.response.data.items[0].errors[0].errorMessage);
        } else {
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              message: 'Aconteceu um erro inesperado. Tente novamente'
            })
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onCloseReasonModal = () => {
    setCancelReasonMessage('');
    setShowErrorReason(false);
    setOpenCancelReasonModal(false);
    onClose();
  };

  return (
    <>
      {!errorMessage && !openCancelReasonModal && (
        <CancelOrderModal
          onClose={onClose}
          onConfirm={async () => {
            const validation = await handleValidation();
            if (validation && validation?.changeAllowed) {
              setOpenCancelReasonModal(true);
            } else if (validation && validation?.reasonNotAllowed) {
              setErrorMessage(validation.reasonNotAllowed);
            } else {
              setErrorMessage(EModalMessageErrorType.PROCCESS);
            }
          }}
          loading={loading}
        />
      )}

      {!errorMessage && openCancelReasonModal && (
        <CancelOrderModalReason
          loading={loading}
          message={cancelReasonMessage}
          setMessage={setCancelReasonMessage}
          showErrorMessage={showErrorReason}
          onConfirm={handleCancelOrder}
          onCancel={!loading ? onCloseReasonModal : undefined}
        />
      )}

      {errorMessage && (
        <WarningCancelOrderModal
          messageType={errorMessage}
          onClose={onClose}
          order={order}
        />
      )}
    </>
  );
};

export default CancelOrder;
